/**
 * BuildMenu.js
 *
 * Phase 11 — Build menu HUD overlay (opens with B key). Lists the structure
 * catalog with cost in nanites; clicking a card enters build-preview mode
 * (a ghost mesh follows the player's aim until they click to commit).
 *
 * Composition:
 *   - parent (Explore3D) controls `open` state and supplies handlers
 *   - this component just lays out the catalog + ESC to close
 */

import React, { useCallback, useEffect } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Hammer, X, Radar, Package, Pickaxe, BatteryCharging, Crosshair, Sparkles,
  Wind, Zap, Lightbulb, Factory, Store, Database, Home,
} from 'lucide-react';

const ICONS = {
  beacon: Radar,
  storage_crate: Package,
  auto_miner: Pickaxe,
  refuel_pad: BatteryCharging,
  turret: Crosshair,
  oxygen_generator: Wind,
  power_generator: Zap,
  floodlight: Lightbulb,
  refinery: Factory,
  trade_terminal: Store,
  cargo_silo: Database,
  heavy_turret: Crosshair,
  habitat: Home,
};

export function BuildMenu({
  open,
  onClose,
  catalog = [],          // array of {id,label,description,cost_nanites}
  nanites = 0,
  buildState,            // { active: bool, typeId: string|null, valid: bool }
  onPreview,             // (typeId) => start preview
  onCommit,              // () => commit current preview
  onCancel,              // () => stop preview
  onBuyNanitesPack,      // () => buy 1 pack of 10 nanites (50 cr)
  credits = 0,
}) {
  // Keep ESC pickup local (parent also handles globally for layered overlays)
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault?.();
        if (buildState?.active) onCancel?.(); else onClose?.();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose, onCancel, buildState]);

  const handlePreview = useCallback((id) => {
    if (typeof onPreview === 'function') onPreview(id);
  }, [onPreview]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[2050] pointer-events-none"
      data-testid="build-menu"
    >
      {/* Side drawer at the bottom (so the canvas stays mostly visible) */}
      <div
        className="absolute left-1/2 -translate-x-1/2 bottom-6 w-[min(960px,96vw)] rounded-2xl border border-amber-300/30 bg-[#040912]/95 backdrop-blur-md shadow-[0_0_0_1px_rgba(252,211,77,0.18)] p-4 pointer-events-auto"
        role="dialog"
        aria-modal="true"
        aria-labelledby="build-menu-title"
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <Hammer className="w-5 h-5 text-amber-300" />
            <h3
              id="build-menu-title"
              className="text-lg text-slate-100 font-bold"
              style={{ fontFamily: 'Space Grotesk, sans-serif' }}
              data-testid="build-menu-title"
            >
              Construct
            </h3>
            <Badge variant="outline" className="border-amber-300/40 text-amber-200 bg-amber-900/10" data-testid="build-menu-nanites">
              <Sparkles className="w-3.5 h-3.5 mr-1" />
              {nanites} nanites
            </Badge>
            {typeof onBuyNanitesPack === 'function' && (
              <Button
                size="sm"
                variant="outline"
                onClick={onBuyNanitesPack}
                disabled={credits < 50}
                data-testid="build-buy-nanites-btn"
                className="text-amber-200 border-amber-300/30 hover:bg-amber-900/20"
              >
                Buy 10 nanites · 50 cr
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            {buildState?.active && (
              <>
                <Button
                  size="sm"
                  onClick={onCancel}
                  variant="outline"
                  data-testid="build-cancel-btn"
                >
                  Cancel Preview
                </Button>
                <Button
                  size="sm"
                  onClick={onCommit}
                  disabled={!buildState?.valid}
                  className="bg-amber-500/90 hover:bg-amber-400 text-slate-950 font-semibold disabled:bg-slate-700 disabled:text-slate-400"
                  data-testid="build-commit-btn"
                >
                  Place ({buildState?.typeId})
                </Button>
              </>
            )}
            <Button variant="outline" size="sm" onClick={onClose} data-testid="build-menu-close">
              <X className="w-4 h-4 mr-2" /> Close
            </Button>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
          {catalog.map((item) => {
            const Icon = ICONS[item.id] || Sparkles;
            const affordable = nanites >= item.cost_nanites;
            const isActive = buildState?.active && buildState?.typeId === item.id;
            return (
              <button
                key={item.id}
                onClick={() => handlePreview(item.id)}
                disabled={!affordable}
                data-testid={`build-card-${item.id}`}
                className={[
                  'text-left rounded-lg border p-3 transition-colors',
                  isActive
                    ? 'border-amber-300/60 bg-amber-900/15'
                    : affordable
                      ? 'border-slate-700/60 bg-slate-900/40 hover:border-amber-300/40 hover:bg-amber-900/10'
                      : 'border-slate-800/60 bg-slate-900/20 opacity-60 cursor-not-allowed',
                ].join(' ')}
              >
                <div className="flex items-center justify-between gap-2 mb-1">
                  <div className="flex items-center gap-2 min-w-0">
                    <Icon className={`w-5 h-5 ${isActive ? 'text-amber-300' : 'text-slate-300'}`} />
                    <span className="text-slate-100 font-medium truncate">{item.label}</span>
                  </div>
                  <Badge variant="outline" className={`shrink-0 ${affordable ? 'border-amber-300/40 text-amber-200' : 'border-slate-700 text-slate-400'}`}>
                    {item.cost_nanites} nan
                  </Badge>
                </div>
                <p className="text-xs text-slate-400 line-clamp-3">{item.description}</p>
                {isActive && (
                  <p className="text-[11px] mt-2 text-amber-300/90">
                    {buildState?.valid ? 'Aim where you want to place · LMB to confirm' : 'Acquiring placement…'}
                  </p>
                )}
              </button>
            );
          })}
        </div>
        <p className="text-[10px] text-slate-500 mt-3 tracking-[0.2em] uppercase text-center">
          Press B again to close · ESC cancels preview · LMB to place
        </p>
      </div>
    </div>
  );
}

export default BuildMenu;
